/**
 * /admin/config — platform settings page (#429 Wave 3 Track D /
 * #410), trimmed down to feature flags + email sender configuration
 * after the SuperAdmin → Agents sub-nav reshape extracted the
 * agent-behaviour sections (Agent models, Default run budgets)
 * into `/admin/agents/*`.
 *
 * What lives here now:
 *
 *      is sectioned so future flags land alongside without a layout
 *      rebuild).
 *   2. **Email sender + subject** — surface-level email tweaks:
 *      subject prefix (prepended with a trailing space) and sender
 *      display name. Plus a read-only inventory of the email
 *      templates defined in code, so an operator can see at a glance
 *      what the system can send without grep'ing the repo. Body
 *      content lives in React Email components under
 *      `apps/server/src/infrastructure/email/templates/` and is
 *      deliberately not operator-editable; ADR 0012's notification
 *      system can revisit this later.
 *
 * What moved out (now under `/admin/agents/*`):
 *
 *   - **Agent models** → `/admin/agents/models`
 *   - **Default run budgets** → `/admin/agents/budgets`
 *
 * Each section shows the cascade-resolved value with provenance
 * ("doc" vs "env" vs "builtIn" vs "unset") so the admin can see at a
 * glance what's currently in effect and where it came from. The form
 * binds to the doc (not the resolved snapshot) — submitting an empty
 * field clears the doc override and the resolver falls through to
 * env.
 *
 * Save is per-section: each section's form submits ONLY its own
 * field group, so an admin saving "feature flags" doesn't accidentally
 * overwrite a half-edited "email" draft. Backend PUT honours partial
 * bodies (each top-level group in the request fully replaces the
 * prior persisted value for that group).
 */
import { useEffect, useState, type FormEvent } from 'react'
import { Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Input, Label, Textarea } from '@/components/ui'
import {
  adminAppSettingsAPI,
  type AppSettingsResponse,
  type EmailTemplateOverrides,
  type PlannerRagConfig,
  type TestSimilarityConfig,
  type InputVariationsConfig,
  type InternalAddressesSetting,
  type ReferenceDocLinkSettings,
  type ResolvedAppSettings,
} from '@/lib/api'
import { Provenance } from '../components/Provenance'
import { DOCS_BASE_URL } from '@/help/HelpPanel'

export function ConfigPage() {
  const [data, setData] = useState<AppSettingsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  async function reload(): Promise<void> {
    setLoading(true)
    try {
      const res = await adminAppSettingsAPI.get()
      setData(res)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load app settings')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  if (loading && !data) {
    return <p className="text-sm text-muted-foreground">Loading platform settings…</p>
  }
  if (error && !data) {
    return (
      <p className="text-sm text-destructive" role="alert">
        {error}
      </p>
    )
  }
  if (!data) return null

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold">Runtime Config</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Platform feature flags and email sender configuration. Agent-behaviour
          knobs (per-agent models, run budgets, prompts) moved to{' '}
          <strong>Admin → Agents</strong>. Each field below cascades{' '}
          <strong>doc &gt; env &gt; built-in default</strong>; clearing a doc
          value falls back to the next tier.
        </p>
      </div>

      {/* First on the page: the one card here that decides what
          OttoTester may connect to. Absent when an older backend
          doesn't serve it. */}
      {data.resolved.referenceDocLinks && (
        <ReferenceDocLinksSection
          doc={data.referenceDocLinks}
          resolved={data.resolved.referenceDocLinks}
          onSaved={reload}
        />
      )}
      <PlannerRagSection doc={data.plannerRag} resolved={data.resolved} onSaved={reload} />
      <TestSimilaritySection doc={data.testSimilarity} resolved={data.resolved} onSaved={reload} />
      <InputVariationsSection doc={data.inputVariations} resolved={data.resolved} onSaved={reload} />
      <EmailTemplatesSection doc={data.emailTemplates} resolved={data.resolved} onSaved={reload} />
    </div>
  )
}

// ── Section: planner RAG augmentation (Slice 3B of #860) ──────────────

/** Knobs that gate + tune the retrieval-driven augmentation pathway
 * that replaced Slice 2's raw `## REFERENCE DOCUMENTS` injection.
 * Four fields; each is optional at the doc tier — clearing a value
 * falls through to env then to the built-in default surfaced in
 * `resolved.plannerRag.defaults`. */
function PlannerRagSection({
  doc,
  resolved,
  onSaved,
}: {
  doc: PlannerRagConfig | undefined
  resolved: ResolvedAppSettings
  onSaved: () => Promise<void>
}) {
  // Form state binds to the doc tier; `null` / `''` means "no
  // override at the doc tier — fall through to env/built-in." Each
  // input pre-fills with the resolved value so the operator sees
  // what's actually running, but submitting a cleared field
  // unpins the doc tier rather than persisting the resolved value
  // (otherwise an unedited form would silently shadow env defaults).
  const [augmentationEnabled, setAugmentationEnabled] = useState<boolean | null>(
    doc?.augmentationEnabled ?? null,
  )
  const [similarityThreshold, setSimilarityThreshold] = useState<string>(
    doc?.similarityThreshold !== undefined ? String(doc.similarityThreshold) : '',
  )
  const [hardKCap, setHardKCap] = useState<string>(
    doc?.hardKCap !== undefined ? String(doc.hardKCap) : '',
  )
  const [backgroundCap, setBackgroundCap] = useState<string>(
    doc?.backgroundTotalCapChars !== undefined ? String(doc.backgroundTotalCapChars) : '',
  )
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  const r = resolved.plannerRag

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const plannerRag: PlannerRagConfig = {}
      if (augmentationEnabled !== null) plannerRag.augmentationEnabled = augmentationEnabled
      if (similarityThreshold.trim()) {
        const n = parseFloat(similarityThreshold)
        if (!Number.isFinite(n) || n < 0 || n > 1) throw new Error('Similarity threshold must be in [0, 1]')
        plannerRag.similarityThreshold = n
      }
      if (hardKCap.trim()) {
        const n = parseInt(hardKCap, 10)
        if (!Number.isFinite(n) || !Number.isInteger(n) || n <= 0) throw new Error('K cap must be a positive integer')
        plannerRag.hardKCap = n
      }
      if (backgroundCap.trim()) {
        const n = parseInt(backgroundCap, 10)
        if (!Number.isFinite(n) || !Number.isInteger(n) || n <= 0) throw new Error('Background char cap must be a positive integer')
        plannerRag.backgroundTotalCapChars = n
      }
      await adminAppSettingsAPI.update({ plannerRag })
      setSavedAt(Date.now())
      await onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Planner Agent Memory augmentation</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5" aria-label="planner-rag">
          <p className="text-xs text-muted-foreground">
            Knobs that gate + tune the retrieval-driven candidate injection
            that runs at planner kickoff. Defaults are good starting points;
            tune the retrieval threshold based on what the{' '}
            <code>candidateRetrievals</code> log shows in production runs.
            Clearing any field falls through to <strong>env &gt; built-in</strong>.
          </p>

          {/* Kill switch */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Checkbox
                id="augmentationEnabled"
                // State is `null` until the operator toggles (so an unedited
                // save never pins an inherited value as a doc override). A
                // checkbox can't render the inherit state, so for DISPLAY we
                // fall back to the resolved value — otherwise an inherited
                // `true` rendered as unchecked, contradicting the "Currently"
                // line below (a real bug an operator reported).
                checked={(augmentationEnabled ?? r.augmentationEnabled) === true}
                onCheckedChange={(v) => setAugmentationEnabled(v)}
                disabled={submitting}
              />
              <Label htmlFor="augmentationEnabled">
                Use retrieved candidate tests to guide planning (off: send the raw reference-document text instead)
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Currently: <strong>{String(r.augmentationEnabled)}</strong>
              <span className="ml-2 text-muted-foreground">(default: {String(r.defaults.augmentationEnabled)})</span>
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={() => setAugmentationEnabled(null)}
              disabled={submitting || augmentationEnabled === null}
            >
              Clear override (fall through to env)
            </Button>
          </div>

          {/* Retrieval relevance threshold — renamed from the bare
              "Similarity threshold" so it isn't confused with the
              test-DUPLICATE threshold below (different job, same cosine
              shape). This one gates which retrieved memory items are
              relevant enough to feed the planner. */}
          <div className="space-y-1">
            <Label htmlFor="similarityThreshold">Memory retrieval threshold (0–1)</Label>
            <Input
              id="similarityThreshold"
              type="number"
              step="0.01"
              min={0}
              max={1}
              value={similarityThreshold}
              placeholder={String(r.defaults.similarityThreshold)}
              onChange={(e) => setSimilarityThreshold(e.target.value)}
              disabled={submitting}
            />
            <p className="text-xs text-muted-foreground">
              Currently: <strong>{r.similarityThreshold}</strong>
              <span className="ml-2 text-muted-foreground">(default: {r.defaults.similarityThreshold})</span>
            </p>
          </div>

          {/* Hard K cap */}
          <div className="space-y-1">
            <Label htmlFor="hardKCap">Hard K cap (max candidates injected)</Label>
            <Input
              id="hardKCap"
              type="number"
              step={1}
              min={1}
              value={hardKCap}
              placeholder={String(r.defaults.hardKCap)}
              onChange={(e) => setHardKCap(e.target.value)}
              disabled={submitting}
            />
            <p className="text-xs text-muted-foreground">
              Currently: <strong>{r.hardKCap}</strong>
              <span className="ml-2 text-muted-foreground">(default: {r.defaults.hardKCap})</span>
            </p>
          </div>

          {/* Background char cap */}
          <div className="space-y-1">
            <Label htmlFor="backgroundCap">Background context char budget</Label>
            <Input
              id="backgroundCap"
              type="number"
              step={100}
              min={1}
              value={backgroundCap}
              placeholder={String(r.defaults.backgroundTotalCapChars)}
              onChange={(e) => setBackgroundCap(e.target.value)}
              disabled={submitting}
            />
            <p className="text-xs text-muted-foreground">
              Currently: <strong>{r.backgroundTotalCapChars}</strong> chars
              <span className="ml-2 text-muted-foreground">(default: {r.defaults.backgroundTotalCapChars})</span>
            </p>
          </div>

          {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
          {savedAt && !error && <p className="text-sm text-success" key={savedAt}>Saved.</p>}

          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save planner Agent Memory settings'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

// ── Section: feature flags ─────────────────────────────────────────────

const CODE_TEMPLATES: ReadonlyArray<{ name: string; summary: string }> = [
  { name: 'Invitation', summary: 'Invitation to join a workspace' },
  { name: 'PasswordReset', summary: 'Password reset request' },
  { name: 'VerifyEmail', summary: 'Initial email verification' },
  { name: 'VerifyEmailChange', summary: 'Email-address change verification' },
  { name: 'Welcome', summary: 'Post-signup welcome' },
]

function EmailTemplatesSection({
  doc,
  resolved,
  onSaved,
}: {
  doc: EmailTemplateOverrides | undefined
  resolved: ResolvedAppSettings
  onSaved: () => Promise<void>
}) {
  const [draft, setDraft] = useState({
    subjectPrefix: doc?.subjectPrefix ?? '',
    senderName: doc?.senderName ?? '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const emailTemplates: EmailTemplateOverrides = {}
      if (draft.subjectPrefix.trim()) emailTemplates.subjectPrefix = draft.subjectPrefix.trim()
      if (draft.senderName.trim()) emailTemplates.senderName = draft.senderName.trim()
      await adminAppSettingsAPI.update({ emailTemplates })
      setSavedAt(Date.now())
      await onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Email sender + subject</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <form onSubmit={handleSubmit} className="space-y-4" aria-label="email-templates">
          <p className="text-xs text-muted-foreground">
            Surface-level only — subject prefix (with a trailing space) and the display name in the From header.
            The body of every email is code, not operator-editable; see the inventory below.
          </p>
          <div className="space-y-1">
            <Label htmlFor="subjectPrefix">Subject prefix</Label>
            <Input
              id="subjectPrefix"
              value={draft.subjectPrefix}
              placeholder="[Staging]"
              onChange={(e) => setDraft((d) => ({ ...d, subjectPrefix: e.target.value }))}
              disabled={submitting}
            />
            <Provenance label="Currently" source={resolved.emailTemplates.subjectPrefix.source}>
              {resolved.emailTemplates.subjectPrefix.value ?? '— (no prefix)'}
            </Provenance>
          </div>
          <div className="space-y-1">
            <Label htmlFor="senderName">Sender display name</Label>
            <Input
              id="senderName"
              value={draft.senderName}
              placeholder="OttoTester"
              onChange={(e) => setDraft((d) => ({ ...d, senderName: e.target.value }))}
              disabled={submitting}
            />
            <Provenance label="Currently" source={resolved.emailTemplates.senderName.source}>
              {resolved.emailTemplates.senderName.value ?? '— (parsed from EMAIL_FROM)'}
            </Provenance>
          </div>

          {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
          {savedAt && !error && <p className="text-sm text-success" key={savedAt}>Saved.</p>}

          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save email overrides'}
          </Button>
        </form>

        <section
          aria-labelledby="email-code-templates-heading"
          className="space-y-2 border-t pt-4"
        >
          <h3
            id="email-code-templates-heading"
            className="text-sm font-semibold"
          >
            Templates defined in code
          </h3>
          <p className="text-xs text-muted-foreground">
            These bodies live as JSX components under{' '}
            <code>apps/server/src/infrastructure/email/templates/</code> and
            ship with the build. Operator-editable bodies are deliberately
            out of scope today; if that changes (e.g. when ADR 0012's
            notification system lands), this section will gain an edit
            affordance then.
          </p>
          <ul className="text-xs space-y-1 mt-2">
            {CODE_TEMPLATES.map((t) => (
              <li key={t.name} className="flex items-baseline gap-2">
                <code className="font-mono text-foreground">{t.name}</code>
                <span className="text-muted-foreground">— {t.summary}</span>
              </li>
            ))}
          </ul>
        </section>
      </CardContent>
    </Card>
  )
}

// ── Section: test similarity (#1626) ─────────────────────────────────
//
// The test-DEDUPLICATION threshold — sits next to the planner-memory
// RETRIEVAL threshold above (#1626 follow-up) so the two cosine knobs
// are adjacent and clearly distinct. Different job: this one decides
// when two TESTS count as the same scenario.

function TestSimilaritySection({
  doc,
  resolved,
  onSaved,
}: {
  doc: TestSimilarityConfig | undefined
  resolved: ResolvedAppSettings
  onSaved: () => Promise<void>
}) {
  const [draft, setDraft] = useState(
    doc?.duplicateThreshold != null ? String(doc.duplicateThreshold) : '',
  )
  // #1786 — dedup judge toggle. Default ON when the doc hasn't set it.
  const [judgeEnabled, setJudgeEnabled] = useState(doc?.judgeEnabled ?? true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      // This section owns the whole `testSimilarity` group (the backend
      // replaces it wholesale). `judgeEnabled` is always sent;
      // `duplicateThreshold` is included only when the input is non-empty —
      // clearing it intentionally unsets the doc value so it falls back to
      // env/built-in.
      const out: TestSimilarityConfig = { judgeEnabled }
      const trimmed = draft.trim()
      if (trimmed) {
        const n = Number(trimmed)
        if (!Number.isFinite(n) || n < 0 || n > 1) {
          setError('Duplicate threshold must be a number between 0 and 1')
          setSubmitting(false)
          return
        }
        out.duplicateThreshold = n
      }
      await adminAppSettingsAPI.update({ testSimilarity: out })
      setSavedAt(Date.now())
      await onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSubmitting(false)
    }
  }

  const r = resolved.testSimilarity.duplicateThreshold
  return (
    <Card>
      <CardHeader>
        <CardTitle>Test similarity</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4" aria-label="test-similarity">
          <div className="space-y-1">
            <Label htmlFor="duplicateThreshold">Duplicate threshold (0–1)</Label>
            <p className="text-xs text-muted-foreground">
              How close two tests must be to count as the same scenario. Drives the run-time
              deduplication and the candidate picker’s “already covered” badge.
            </p>
            <Input
              id="duplicateThreshold"
              type="number"
              step="0.01"
              value={draft}
              placeholder="0.88"
              onChange={(e) => setDraft(e.target.value)}
              disabled={submitting}
              className="w-40"
            />
            <Provenance label="App-default tier" source={r.source}>
              {/* When unset at the doc tier the effective value comes from
                  the TESTCASE_SEMANTIC_DEDUP_THRESHOLD env var if set, else
                  the built-in 0.88 — so don't claim "built-in" outright. */}
              {r.value != null ? String(r.value) : '— (env var or built-in default 0.88)'}
            </Provenance>
          </div>

          {/* #1786 — dedup judge toggle. */}
          <label className="flex items-start gap-2">
            <Checkbox checked={judgeEnabled} onCheckedChange={(v) => setJudgeEnabled(v === true)} disabled={submitting} />
            <span className="text-sm leading-snug">
              Use the AI judge for borderline duplicates
              <span className="block text-xs text-muted-foreground">
                On (default): when two tests look similar enough to be flagged, an AI check decides whether they really test the same thing before one is skipped — it can only keep a test, never drop an extra one. Off: a test that scores above the threshold is skipped on the score alone.
              </span>
            </span>
          </label>

          {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
          {savedAt && !error && <p className="text-sm text-success" key={savedAt}>Saved.</p>}

          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

// ── Section: input variations (#1800) ────────────────────────────────
//
// The per-field cap on how many valid input classes the planner derives
// when "Add input variations" is on. Sibling of the planner RAG hard-K cap
// above — both bound how much the planner emits per run.

function InputVariationsSection({
  doc,
  resolved,
  onSaved,
}: {
  doc: InputVariationsConfig | undefined
  resolved: ResolvedAppSettings
  onSaved: () => Promise<void>
}) {
  const [draft, setDraft] = useState(
    doc?.maxClassesPerField != null ? String(doc.maxClassesPerField) : '',
  )
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const out: InputVariationsConfig = {}
      const trimmed = draft.trim()
      if (trimmed) {
        const n = Number(trimmed)
        // Client-side mirror of the server bound [1, 50]; the server is
        // authoritative and rejects out-of-range values too.
        if (!Number.isInteger(n) || n < 1 || n > 50) {
          setError('Max classes per field must be a whole number between 1 and 50')
          setSubmitting(false)
          return
        }
        out.maxClassesPerField = n
      }
      await adminAppSettingsAPI.update({ inputVariations: out })
      setSavedAt(Date.now())
      await onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSubmitting(false)
    }
  }

  const r = resolved.inputVariations
  return (
    <Card>
      <CardHeader>
        <CardTitle>Input variations</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4" aria-label="input-variations">
          <div className="space-y-1">
            <Label htmlFor="maxClassesPerField">Max classes per field (1–50)</Label>
            <p className="text-xs text-muted-foreground">
              When “Add input variations” is on, the most valid input classes the planner will
              derive for a single field — each becomes one Test Data row. Higher = more coverage
              per field, but more rows and more run time under “All rows”.
            </p>
            <Input
              id="maxClassesPerField"
              type="number"
              step="1"
              min={1}
              max={50}
              value={draft}
              placeholder="6"
              onChange={(e) => setDraft(e.target.value)}
              disabled={submitting}
              className="w-40"
            />
            <Provenance label="Currently" source={r.source}>
              {String(r.value)}
            </Provenance>
          </div>

          {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
          {savedAt && !error && <p className="text-sm text-success" key={savedAt}>Saved.</p>}

          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

// ── Section: which sites reference-doc links can reach ───────────────

const INTERNAL_ADDRESS_LABELS: Record<InternalAddressesSetting, string> = {
  blocked: 'Public sites only',
  allowed: 'Public sites and your internal network',
}

/** "Links OttoTester can read" — built to the approved mockup
 * (`docs/mockups/2935-links-ottotester-can-read.html`). The choice
 * binds to the saved value, or `null` when nothing is saved and the
 * install's own setting applies; the radios then show that setting.
 * "Reset to default settings" fills the card with the defaults (no
 * saved choice, both lists empty) and saves nothing until Save. The
 * server checks every list line and sends back a message naming the
 * line, which this card shows as it is. */
function ReferenceDocLinksSection({
  doc,
  resolved,
  onSaved,
}: {
  doc: ReferenceDocLinkSettings | undefined
  resolved: NonNullable<ResolvedAppSettings['referenceDocLinks']>
  onSaved: () => Promise<void>
}) {
  const [choice, setChoice] = useState<InternalAddressesSetting | null>(doc?.internalAddresses ?? null)
  const [alwaysAllow, setAlwaysAllow] = useState((doc?.alwaysAllow ?? []).join('\n'))
  const [neverRead, setNeverRead] = useState((doc?.neverRead ?? []).join('\n'))
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  const shown = choice ?? resolved.defaultInternalAddresses
  const showsDefaults = choice === null && !alwaysAllow.trim() && !neverRead.trim()

  function resetToDefaults() {
    setChoice(null)
    setAlwaysAllow('')
    setNeverRead('')
    setSavedAt(null)
    setError(null)
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      await adminAppSettingsAPI.update({
        referenceDocLinks: {
          internalAddresses: choice,
          alwaysAllow: alwaysAllow.split('\n'),
          neverRead: neverRead.split('\n'),
        },
      })
      setSavedAt(Date.now())
      await onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Links OttoTester can read</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5" aria-label="reference-doc-links">
          <p className="text-xs text-muted-foreground">
            When someone adds a link under Tests → Proposed, OttoTester downloads that page to read
            it. Choose which sites it may download from. This applies to every organization and
            workspace on this install, and every change is recorded in the Audit Log.
          </p>

          <div className="space-y-2">
            <Label>Sites links can reach</Label>
            <div className="space-y-1.5 text-sm">
              {(['blocked', 'allowed'] as const).map((value) => (
                <label key={value} className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="internalAddresses"
                    className="mt-1"
                    checked={shown === value}
                    onChange={() => setChoice(value)}
                    disabled={submitting}
                  />
                  <span>
                    {INTERNAL_ADDRESS_LABELS[value]}
                    {value === 'allowed' && (
                      <span className="block text-xs text-muted-foreground">
                        Private addresses such as 10.x.x.x, 172.16.x.x to 172.31.x.x and 192.168.x.x.
                      </span>
                    )}
                  </span>
                </label>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Whichever you pick, OttoTester never reads the addresses cloud servers use to hand out
              their own passwords. It also skips addresses that lead back to the computer it runs on,
              such as <code>127.0.0.1</code>, and self-assigned <code>169.254.x.x</code> addresses,
              unless you add their address range under Always allow (for example{' '}
              <code>127.0.0.1/32</code>). Addresses like <code>0.0.0.0</code>, broadcast and
              multicast addresses are never read.
            </p>
            <Provenance label="Currently" source={resolved.internalAddresses.source}>
              {INTERNAL_ADDRESS_LABELS[resolved.internalAddresses.value]}
            </Provenance>
          </div>

          <div className="space-y-1">
            <Label htmlFor="alwaysAllow">Always allow</Label>
            <Textarea
              id="alwaysAllow"
              rows={4}
              className="font-mono text-sm"
              value={alwaysAllow}
              placeholder="wiki.corp.local"
              onChange={(e) => setAlwaysAllow(e.target.value)}
              disabled={submitting}
            />
            <p className="text-xs text-muted-foreground">
              Sites to read even when they are inside your network. One per line: a name
              (<code>wiki.corp.local</code>), a name with <code>*</code> for any part of it
              (<code>docs-*.corp.local</code>, or <code>*.corp.local</code> for every name ending in
              .corp.local), or an address range (<code>10.20.0.0/16</code>). Not a page address: leave
              out <code>https://</code> and anything after the name.
            </p>
          </div>

          <div className="space-y-1">
            <Label htmlFor="neverRead">Never read</Label>
            <Textarea
              id="neverRead"
              rows={3}
              className="font-mono text-sm"
              value={neverRead}
              onChange={(e) => setNeverRead(e.target.value)}
              disabled={submitting}
            />
            <p className="text-xs text-muted-foreground">
              Sites to refuse, public or internal, written the same way. If a site is on both lists,
              it is not read.
            </p>
          </div>

          {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
          {savedAt && !error && <p className="text-sm text-success" key={savedAt}>Saved.</p>}

          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save link settings'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={resetToDefaults}
              disabled={submitting || showsDefaults}
            >
              Reset to default settings
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            People whose link is refused see the same message as today. The{' '}
            <a
              href={`${DOCS_BASE_URL}/reference-docs/#links-to-sites-inside-your-network`}
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline"
            >
              Reference docs help
            </a>{' '}
            tells them their admin can change these settings.
          </p>
        </form>
      </CardContent>
    </Card>
  )
}

export default ConfigPage
